import { deriveQuadrant } from './taskMeta';
import { resolveDueDate } from './taskDates';

/**
 * What a new task is, before anyone sends it.
 *
 * Three places create tasks — the board, Home's quick-add dialog, and the chat
 * panel — and until this existed they created three different things. Only the
 * board resolved a due date out of the title, so "ship the paywall friday"
 * became a dated task from /tasks and an undated one from the other two; only
 * the board derived a quadrant, so a high/high task added from Home landed in
 * "Schedule" instead of "Do First"; and the chat panel was the only one setting
 * a difficulty. Same sentence, three outcomes, depending on which box you typed
 * it into.
 *
 * Returns null for a blank title so callers keep their early return.
 */
export function buildNewTask(title, options = {}) {
    const trimmed = String(title ?? '').trim();
    if (!trimmed) return null;

    const priority = options.priority || 'medium';
    const importance = options.importance || 'medium';

    const task = {
        title: trimmed,
        priority,
        importance,
        difficulty: options.difficulty || 'medium',
        status: options.status || 'todo',
        quadrant: options.quadrant || deriveQuadrant(priority, importance),
        // An explicit date wins, including an explicit null — that is how a
        // caller says "no date" rather than "work it out".
        due_date: options.due_date !== undefined
            ? options.due_date
            : resolveDueDate(trimmed, options.description),
    };

    if (options.description !== undefined) task.description = options.description;
    // Born parked — the backlog's quick-add capturing a someday idea that should
    // never touch the board. Sent as a timestamp rather than the boolean the
    // update path uses, because create goes through TaskModel and Pydantic
    // validates the ISO string for us. Absent means active, so every other
    // caller is unaffected.
    if (options.demoted) task.demoted_at = new Date().toISOString();
    return task;
}

/**
 * The same date resolution, for an edit.
 *
 * Asymmetric on purpose: text can *set* a due date and never clear one, because
 * a rename that silently dropped a deadline would be far worse than one that
 * failed to add it. Clearing is the badge's job, and only the badge's.
 *
 * ── The bug this fixes ──────────────────────────────────────────────────────
 * The board used to re-resolve from `updates.title ?? current.title` — the
 * *stored* title when only the notes were edited. So on a task titled "ship it
 * friday" whose date you had cleared on the badge, editing the notes re-parsed a
 * title the edit never touched and brought the date straight back. The comment
 * above it asserted this could not happen. Found by the Python port's test
 * suite, which caught it in the copy before anyone caught it in the original.
 *
 * Reading only the fields actually present in `updates` is the whole fix: text
 * can still set a date, but only the text you changed is read.
 */
export function withResolvedDueDate(updates) {
    if (updates.title === undefined && updates.description === undefined) return updates;
    // An explicit due_date in the same edit is the user's, not the parser's.
    if (updates.due_date !== undefined) return updates;

    const resolved = resolveDueDate(updates.title, updates.description);
    return resolved ? { ...updates, due_date: resolved } : updates;
}
